import React, { useState, useEffect, useCallback, useRef } from 'react';
import './PublicCatalogView.css';
import './PublicCatalogViewEnhancements.css';
import { PrivateCatalogService } from '../../services/privateCatalogService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck, faChevronRight, faSearch } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import Loader from '../Loader/Loader';
import Alert from '../Alert/Alert';
import CartPreviewModal from './CartPreviewModal';
import WhatsAppConfirmModal from './WhatsAppConfirmModal';
import ProductCard from './ProductCard';
import { useCatalogCart } from './hooks/useCatalogCart';
import { shuffleArray, ITEMS_PER_LOAD } from './utils/catalogUtils';
import throttle from 'lodash.throttle';

const PublicCatalogView = ({ catalogId, providerId }) => {
    const [catalogInfo, setCatalogInfo] = useState(null);
    const [productIds, setProductIds] = useState([]);
    const [allProductIds, setAllProductIds] = useState([]);
    const [visibleProducts, setVisibleProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const loadIndexRef = useRef(0);
    const [hasMore, setHasMore] = useState(true);
    const [alert, setAlert] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [showStickyHeader, setShowStickyHeader] = useState(false);
    const [showStickySearch, setShowStickySearch] = useState(false);
    const [showCartPreviewHover, setShowCartPreviewHover] = useState(false);
    const [expandedProductId, setExpandedProductId] = useState(null);

    const {
        cart, setCart,
        selectedProducts, setSelectedProducts,
        creatingOrder,
        showCartPreview, setShowCartPreview,
        showWhatsAppModal,
        showSuccessMessage,
        toggleProductSelection,
        updateCartItem,
        getCartItemsCount,
        getCartTotal,
        getAvailableQuantities,
        getPriceForQuantity,
        handleOpenCartPreview,
        createTemporaryOrder,
        handleWhatsAppConfirm,
        handleWhatsAppCancel,
        handleNewOrder,
    } = useCatalogCart({ catalogInfo, setAlert });

    // Capturar errores globales
    useEffect(() => {
        const handleError = (event) => {
            console.error('Error capturado en PublicCatalogView:', event.error);
            setAlert({ type: 'error', message: 'Ocurrió un error. Por favor recarga la página.' });
        };
        window.addEventListener('error', handleError);
        return () => window.removeEventListener('error', handleError);
    }, []);

    // Cargar catálogo inicial
    const fetchCatalogData = useCallback(async () => {
        try {
            setLoading(true);
            setVisibleProducts([]);
            loadIndexRef.current = 0;
            setHasMore(true);

            const data = await PrivateCatalogService.getCatalogProductIds(catalogId);

            setCatalogInfo({
                banner_url: data.banner_url,
                logo_url: data.logo_url,
                provider_id: data.provider_id,
                telefono: data.telefono,
                nombre_empresa: data.nombre_empresa,
                descripcion: data.descripcion,
                direccion: data.direccion,
                ciudad: data.ciudad,
                departamento: data.departamento,
                correo: data.correo,
                calificacion: data.calificacion,
                name: data.catalog?.name || 'Catálogo de Productos',
                description: data.catalog?.description
            });

            const shuffledIds = shuffleArray(data.productIds || []);
            setProductIds(shuffledIds);
            setAllProductIds(shuffledIds);

            if (shuffledIds.length === 0) { setLoading(false); return; }

            const firstIds = shuffledIds.slice(0, ITEMS_PER_LOAD);
            const previews = await PrivateCatalogService.getProductsByIds(firstIds, catalogId);
            setVisibleProducts(previews);
            loadIndexRef.current = ITEMS_PER_LOAD;
            setHasMore(shuffledIds.length > ITEMS_PER_LOAD);
            setLoading(false);
        } catch (error) {
            console.error('Error al obtener datos del catálogo:', error);
            setCatalogInfo({});
            setVisibleProducts([]);
            setLoading(false);
        }
    }, [catalogId]);

    useEffect(() => {
        if (catalogId) fetchCatalogData();
    }, [catalogId, fetchCatalogData]);

    // Infinite scroll
    const loadMore = useCallback(async () => {
        if (loadingMore || !hasMore || productIds.length === 0) return;
        setLoadingMore(true);
        try {
            const nextIds = productIds.slice(loadIndexRef.current, loadIndexRef.current + ITEMS_PER_LOAD);
            if (nextIds.length === 0) { setHasMore(false); setLoadingMore(false); return; }
            const previews = await PrivateCatalogService.getProductsByIds(nextIds, catalogId);
            setVisibleProducts(prev => [...prev, ...previews]);
            loadIndexRef.current += ITEMS_PER_LOAD;
            if (loadIndexRef.current >= productIds.length) setHasMore(false);
        } catch (error) {
            console.error('Error al cargar más productos:', error);
        } finally {
            setLoadingMore(false);
        }
    }, [productIds, loadingMore, hasMore, catalogId]);

    const handleScroll = useCallback(() => {
        if (loading || loadingMore || !hasMore || isSearching) return;
        if (window.innerHeight + document.documentElement.scrollTop + 200 >= document.documentElement.offsetHeight) {
            loadMore();
        }
    }, [loading, loadingMore, hasMore, isSearching, loadMore]);

    useEffect(() => {
        const throttled = throttle(handleScroll, 200);
        window.addEventListener('scroll', throttled, { passive: true });
        return () => { window.removeEventListener('scroll', throttled); throttled.cancel(); };
    }, [handleScroll]);

    // Búsqueda con debounce
    useEffect(() => {
        const timer = setTimeout(async () => {
            if (searchTerm.trim()) {
                setIsSearching(true);
                setLoadingMore(true);
                try {
                    const data = await PrivateCatalogService.getCatalogProductIds(catalogId, searchTerm);
                    const searchIds = data.productIds || [];
                    setProductIds(searchIds);
                    const products = await PrivateCatalogService.getProductsByIds(searchIds, catalogId);
                    setVisibleProducts(products);
                    loadIndexRef.current = searchIds.length;
                    setHasMore(false);
                } catch (error) {
                    console.error('Error en búsqueda:', error);
                } finally {
                    setIsSearching(false);
                    setLoadingMore(false);
                }
            } else {
                setProductIds(allProductIds);
                loadIndexRef.current = 0;
                setHasMore(true);
                if (allProductIds.length > 0) {
                    setLoadingMore(true);
                    try {
                        const firstIds = allProductIds.slice(0, ITEMS_PER_LOAD);
                        const previews = await PrivateCatalogService.getProductsByIds(firstIds, catalogId);
                        setVisibleProducts(previews);
                        loadIndexRef.current = ITEMS_PER_LOAD;
                        setHasMore(allProductIds.length > ITEMS_PER_LOAD);
                    } catch (error) {
                        console.error('Error al recargar productos:', error);
                    } finally {
                        setLoadingMore(false);
                    }
                }
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm, catalogId, allProductIds]);

    // Sticky header en móvil
    useEffect(() => {
        const onScroll = () => {
            if (window.innerWidth < 768) setShowStickyHeader(window.scrollY > 200);
        };
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const downloadImage = async (imageUrl, productName, imageIndex) => {
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${productName}_imagen_${imageIndex + 1}.jpg`;
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
                if (link.parentNode === document.body) document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 100);
        } catch (error) {
            console.error('Error al descargar imagen:', error);
            window.open(imageUrl, '_blank');
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="private-catalog-public-view">
            {/* Sticky header móvil */}
            {showStickyHeader && catalogInfo && (
                <div className="private-catalog-sticky-header">
                    <div className="sticky-header-content">
                        <div className="sticky-header-logo">
                            {catalogInfo.logo_url ? (
                                <img src={catalogInfo.logo_url} alt={catalogInfo.nombre_empresa || 'Logo'} onError={(e) => { e.target.style.display = 'none'; }} />
                            ) : (
                                <div className="sticky-header-logo-placeholder">{catalogInfo.nombre_empresa?.charAt(0) || 'T'}</div>
                            )}
                        </div>
                        <div className="sticky-header-name">{catalogInfo.nombre_empresa || catalogInfo.name || 'Catálogo'}</div>
                        <button className="sticky-header-search-btn" onClick={() => setShowStickySearch(!showStickySearch)} aria-label="Buscar productos">
                            <FontAwesomeIcon icon={showStickySearch ? faTimes : faSearch} />
                        </button>
                        {catalogInfo.telefono && (
                            <a href={`https://wa.me/${catalogInfo.telefono.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="sticky-header-whatsapp-btn" aria-label="Contactar por WhatsApp">
                                <FontAwesomeIcon icon={faWhatsapp} />
                            </a>
                        )}
                    </div>
                    {showStickySearch && (
                        <div className="sticky-search-bar">
                            <div className="sticky-search-wrapper">
                                <FontAwesomeIcon icon={faSearch} className="sticky-search-icon" />
                                <input type="text" placeholder="Buscar productos..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="sticky-search-input" autoFocus />
                                {searchTerm && (
                                    <button onClick={() => setSearchTerm('')} className="sticky-search-clear" aria-label="Limpiar búsqueda">
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Banner */}
            <div className="private-catalog-banner-container">
                <img
                    src={catalogInfo?.banner_url || 'https://cdn.minymol.com/catalog-banners/banner_defecto.jpg'}
                    alt={catalogInfo?.banner_url ? 'Banner del proveedor' : 'Banner por defecto'}
                    className="private-catalog-banner-image"
                    onError={(e) => {
                        if (e.target.src !== 'https://cdn.minymol.com/catalog-banners/banner_defecto.jpg') {
                            e.target.src = 'https://cdn.minymol.com/catalog-banners/banner_defecto.jpg';
                        } else {
                            e.target.style.display = 'none';
                        }
                    }}
                />
            </div>

            {/* Buscador */}
            <div className="private-catalog-search-container">
                <div className="private-catalog-search-wrapper">
                    <FontAwesomeIcon icon={faSearch} className="private-catalog-search-icon" />
                    <input type="text" placeholder="Buscar productos por nombre o descripción..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="private-catalog-search-input" />
                    {searchTerm && (
                        <button onClick={() => setSearchTerm('')} className="private-catalog-search-clear" aria-label="Limpiar búsqueda">
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    )}
                </div>
                {isSearching && <div className="private-catalog-search-loading">Buscando...</div>}
            </div>

            {/* Carrito flotante */}
            {selectedProducts.size > 0 && cart.length > 0 && (
                <div
                    className="private-catalog-floating-cart-container"
                    onMouseEnter={() => setShowCartPreviewHover(true)}
                    onMouseLeave={() => setShowCartPreviewHover(false)}
                >
                    {showCartPreviewHover && cart.length > 0 && (
                        <div className="private-catalog-cart-quick-preview">
                            <div className="cart-preview-header">
                                <span className="cart-preview-title">Tu pedido</span>
                                <span className="cart-preview-count">{getCartItemsCount()} items</span>
                            </div>
                            <div className="cart-preview-items">
                                {cart.slice(0, 3).map((item, index) => (
                                    <div key={index} className="cart-preview-item">
                                        <img src={item.productImageUrl} alt={item.productName} />
                                        <div className="cart-preview-item-info">
                                            <span className="cart-preview-item-name">{item.productName}</span>
                                            <span className="cart-preview-item-details">
                                                {[item.colorName, item.sizeName, item.quantity ? `${item.quantity} und` : ''].filter(Boolean).join(' • ')}
                                            </span>
                                        </div>
                                        <span className="cart-preview-item-price">${item.totalPrice?.toLocaleString() || '0'}</span>
                                    </div>
                                ))}
                                {cart.length > 3 && <div className="cart-preview-more">+{cart.length - 3} items más</div>}
                            </div>
                            <div className="cart-preview-footer">
                                <span>Total:</span>
                                <span className="cart-preview-total">${getCartTotal().toLocaleString()}</span>
                            </div>
                        </div>
                    )}
                    <button onClick={handleOpenCartPreview} disabled={creatingOrder} className="private-catalog-floating-cart-btn pulsing">
                        <div className="private-catalog-cart-icon"><FontAwesomeIcon icon={faWhatsapp} /></div>
                        <div className="private-catalog-cart-content">
                            <span className="private-catalog-cart-count">{getCartItemsCount()} items | ${getCartTotal().toLocaleString()}</span>
                            <span className="private-catalog-cart-action">Ver resumen del pedido</span>
                        </div>
                        <div className="private-catalog-cart-arrow"><FontAwesomeIcon icon={faChevronRight} /></div>
                    </button>
                </div>
            )}

            {/* Mensaje de éxito */}
            {showSuccessMessage && (
                <div className="private-catalog-success-message">
                    <div className="success-message-content">
                        <div className="success-icon"><FontAwesomeIcon icon={faCheck} /></div>
                        <div className="success-text">
                            <h3>¡Pedido enviado!</h3>
                            <p>Tu pedido fue enviado a WhatsApp exitosamente</p>
                        </div>
                        <button onClick={handleNewOrder} className="new-order-btn">Nuevo pedido</button>
                    </div>
                </div>
            )}

            {/* Grid de productos */}
            <div className="private-catalog-main-container">
                <div className="private-catalog-products-grid">
                    {visibleProducts.length === 0 && !loadingMore ? (
                        <div className="private-catalog-no-products">
                            <div className="no-products-icon"><FontAwesomeIcon icon={searchTerm ? faSearch : faWhatsapp} /></div>
                            <h3 className="no-products-title">{searchTerm ? 'No se encontraron productos' : '¡Ups! Este catálogo está vacío'}</h3>
                            <p className="no-products-message">
                                {searchTerm ? 'No hay productos que coincidan con tu búsqueda.' : 'Parece que este catálogo aún no tiene productos o el enlace no es válido.'}
                            </p>
                            {searchTerm ? (
                                <button onClick={() => setSearchTerm('')} className="no-products-clear-search-btn">Limpiar búsqueda</button>
                            ) : catalogInfo?.telefono && (
                                <a href={`https://wa.me/${catalogInfo.telefono.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="no-products-whatsapp-btn">
                                    <FontAwesomeIcon icon={faWhatsapp} /> Contactar por WhatsApp
                                </a>
                            )}
                        </div>
                    ) : (
                        visibleProducts.map(product => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                isSelected={selectedProducts.has(product.id)}
                                cart={cart}
                                setCart={setCart}
                                setSelectedProducts={setSelectedProducts}
                                onToggleSelection={toggleProductSelection}
                                onDownloadImage={downloadImage}
                                onUpdateCartItem={updateCartItem}
                                getAvailableQuantities={getAvailableQuantities}
                                getPriceForQuantity={getPriceForQuantity}
                                expandedProductId={expandedProductId}
                                onSetExpanded={setExpandedProductId}
                            />
                        ))
                    )}
                </div>

                {loadingMore && hasMore && <div className="private-catalog-loading-more"><Loader /></div>}
                {!hasMore && visibleProducts.length > 0 && !searchTerm && (
                    <div className="private-catalog-end-message">No hay más productos para mostrar</div>
                )}
            </div>

            {/* Modales */}
            {showCartPreview && (
                <CartPreviewModal
                    cart={cart}
                    products={visibleProducts}
                    onClose={() => setShowCartPreview(false)}
                    onConfirm={createTemporaryOrder}
                    creatingOrder={creatingOrder}
                    onUpdateCart={setCart}
                    selectedProducts={selectedProducts}
                    onUpdateSelectedProducts={setSelectedProducts}
                    catalogId={catalogId}
                    getAvailableQuantities={getAvailableQuantities}
                    getPriceForQuantity={getPriceForQuantity}
                />
            )}

            <WhatsAppConfirmModal
                isOpen={showWhatsAppModal}
                onClose={handleWhatsAppCancel}
                onConfirm={handleWhatsAppConfirm}
                itemCount={cart.length}
            />

            {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}
        </div>
    );
};

export default PublicCatalogView;
